import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { CalendarX } from '@phosphor-icons/react';
import { api, humanError } from '../lib/api';
import { dayLabel, dayNumber, fmt, shortWeekday, todayIn } from '../lib/time';

interface Props {
  slug: string;
  serviceId: string;
  timezone: string;
  value: string | null;
  onChange: (iso: string, day: string) => void;
  initialDay?: string | null;
  days?: number;
}

/**
 * Day strip + time grid computed by the server (get_slots): working hours,
 * exceptions, bay occupancy, service duration and buffer. Busy times are
 * shown crossed out, not hidden, so the client sees the real picture.
 */
export function SlotPicker({ slug, serviceId, timezone, value, onChange, initialDay, days = 14 }: Props) {
  const from = todayIn(timezone);
  const q = useQuery({
    queryKey: ['slots', slug, serviceId, from, days],
    queryFn: () => api.slots(slug, serviceId, from, days),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
  const firstOpen = useMemo(() => q.data?.days.find((d) => d.slots.some((s) => s.available))?.date, [q.data]);
  const [day, setDay] = useState<string | null>(initialDay ?? null);
  const activeDay = day ?? firstOpen ?? q.data?.days[0]?.date ?? null;
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stripRef.current?.querySelector('[aria-pressed="true"]') as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [activeDay]);

  if (q.isPending) {
    return (
      <VStack gap={3} aria-busy>
        <HStack gap={2}>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} width={58} height={68} index={i} />
          ))}
        </HStack>
        <Skeleton height={160} />
      </VStack>
    );
  }
  if (q.isError) {
    return (
      <EmptyState
        isCompact
        title="Не удалось загрузить время"
        description={humanError(q.error)}
        actions={<Button label="Повторить" variant="primary" clickAction={() => q.refetch().then(() => undefined)} />}
      />
    );
  }
  const data = q.data;
  const current = data.days.find((d) => d.date === activeDay);
  const visible = current?.slots.filter((s) => s.reason !== 'past') ?? [];
  const anyFree = data.days.some((d) => d.slots.some((s) => s.available));

  return (
    <VStack gap={4}>
      <div className="day-strip" ref={stripRef} role="group" aria-label="Дата">
        {data.days.map((d) => {
          const free = d.slots.filter((s) => s.available).length;
          return (
            <button
              key={d.date}
              type="button"
              className="day-chip"
              aria-pressed={d.date === activeDay}
              disabled={d.closed}
              aria-label={`${dayLabel(d.date, timezone)}${d.closed ? ', выходной' : `, свободно ${free}`}`}
              onClick={() => setDay(d.date)}
            >
              <span className="day-chip__wd">{shortWeekday(d.date)}</span>
              <span className="day-chip__d">{dayNumber(d.date)}</span>
            </button>
          );
        })}
      </div>

      {!anyFree ? (
        <EmptyState
          isCompact
          icon={<CalendarX size={32} aria-hidden />}
          title="Свободного времени нет"
          description="На ближайшие две недели всё занято. Позвоните в студию — подберём вариант."
        />
      ) : current?.closed ? (
        <Text color="secondary">В этот день студия не работает.</Text>
      ) : visible.length === 0 ? (
        <Text color="secondary">На этот день время закончилось — выберите другой день.</Text>
      ) : (
        <VStack gap={3}>
          <Text weight="semibold">{current ? dayLabel(current.date, timezone) : ''}</Text>
          <div className="slot-grid" role="group" aria-label="Время">
            {visible.map((s) => (
              <button
                key={s.starts_at}
                type="button"
                className="slot"
                aria-pressed={value === s.starts_at}
                disabled={!s.available}
                aria-label={`${s.time}${s.available ? '' : ', занято'}`}
                onClick={() => onChange(s.starts_at, current!.date)}
              >
                <span className="slot__time">{s.time}</span>
                {!s.available && <span className="slot__note">занято</span>}
              </button>
            ))}
          </div>
          <HStack gap={4} wrap="wrap">
            <Text type="supporting">
              <span className="legend-swatch" aria-hidden /> свободно
            </Text>
            <Text type="supporting">
              <span className="legend-swatch legend-swatch--busy" aria-hidden /> занято
            </Text>
            <Text type="supporting">
              <span className="legend-swatch legend-swatch--sel" aria-hidden /> выбрано
            </Text>
          </HStack>
          <Text type="supporting">Время студии: {fmt(new Date(), timezone, 'HH:mm')} ({timezone.split('/').pop()?.replace('_', ' ')})</Text>
        </VStack>
      )}
    </VStack>
  );
}
